import { Locale } from '@/translations/main'

export const enLocale: Locale = {
  button: {
    save: 'Save',
    ok: 'Ok',
    cancel: 'Cancel',
    close: 'Close',
    add: 'Add',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    view: 'View',
    viewUsages: 'View uses',
    copy: 'Copy',
    next: 'Next',
    previous: 'Previous',
    discard: 'Discard',
    archive: 'Archive',
    unarchive: 'Unarchive',
  },
  notification: {
    failed: 'Something went wrong',
    saved: 'Saved',
    deleted: 'Deleted',
    duplicated: 'Duplicated',
    deleteTitle: 'Delete {name}?',
    deleteWarningTitle: 'Be careful',
    deleteHindranceText: 'I understand that deleting this item is dangerous and irreversible',
  },
  language: {
    NL: 'Dutch',
    EN: 'English',
  },
  aiFeature: {
    STEP_PROPOSALS_FROM_MEDIA: 'Video/audio analyser: create steps from video and audio files',
    STEP_PROPOSALS_FROM_DOCUMENT: 'Document analyser: create steps from PDF and Word documents',
  },
  navigationDrawer: {
    platform: 'Platform',
    organization: 'Organization',
    currentUploads: 'Current uploads',
  },
  section: {
    processEditor: 'Process Editor',
    operations: 'Operations',
    checks: 'Checks',
    issues: 'Issues',
  },
  view: {
    home: {
      welcome: 'Welcome!',
      selectOrganization: 'Select an organization to get started',
      buttonGo: 'Go',
    },
    issueLabel: {
      singular: 'Issue Label',
      plural: 'Issue Labels',
      deleteWarning:
        'Cannot delete a label that is assigned to issues, please remove the label from the issues first.',
      field: {
        name: 'Name',
        color: 'Color',
      },
    },
    organization: {
      bops: {
        buttonCreateEmpty: 'Create empty BOP',
        buttonDuplicate: 'Duplicate BOP',
        cannotCreateMessage:
          'No new version can be created as long as the last version is not released.',
        releaseTooltip: 'Release this version',
      },
      library: {
        title: 'Library',
        libraryStep: 'Library Step',
        createStep: 'Create Library Step',
        lastModified: 'Last modified',
        usage: 'Usage',
        emptySteps: 'No library steps yet. Create your first library step.',
        whereUsedTitle: 'Where Used',
        usedInBops: 'Used in {count} BOP(s)',
        notInUse: 'Not currently in use',
        versions: 'Versions',
        releasedAt: 'Released on',
        releasedReadOnly: 'Released versions are read-only. Create a draft to make changes.',
        versionReleased: 'v{version} released',
        onOlderVersions: '{count} on older versions',
        releaseTitle: 'Release library step',
        releaseMessage:
          "You're about to release version {version} of library step {stepName}. This is irreversible: the version becomes read-only and can be added to BOPs. BOPs that use an older version keep using it. Are you sure?",
      },
      bopProcess: {
        downloadMbomButton: 'Download MBOM',
        downloadWorkInstructionsButton: 'Export Work Instructions',
        menuView: 'Go to module',
        menuAddChild: 'Add next',
        menuAddParent: 'Link to previous',
        menuUpdateNode: 'Edit module details',
        menuDeleteParent: 'Unlink from previous',
        menuDeleteNode: 'Delete module',
        menuMoveAbove: 'Move above',
      },
      home: {
        productDescription: 'Define the Bills of Process for your products.',
        operatorTitle: 'Operator home',
        operatorDescription: 'View instructions to create a module or create a production batch.',
        issueDescription: 'Report a new issue or look at current issues reported by others.',
        productionBatchDescription:
          'View past production batches and find the as-built status of your products.',
      },
      module: {
        downloadMbomButton: 'Download MBOM',
        downloadWorkInstructionsButton: 'Export Work Instructions',
      },
      operatorProduct: {
        title: 'Products',
        titleBatchesInProgress: 'Work in progress',
        noData: 'No products found',
      },
      settings: {
        title: 'Company settings',
        tabAi: 'AI',
        aiExplanation:
          'Choose which AI features are available in your company. Switched off features are hidden from and cannot be used by anyone in your company.',
      },
      operatorProductConfiguration: {
        noData: 'No configurations found',
        bopSelectorHint: 'Only released BOPs can be selected',
        buttonCreateProductionBatch: 'Produce batch',
        buttonViewModule: 'View',
      },
    },
  },
  component: {
    accountMenu: {
      switchOrganization: 'Switch organization',
      logOut: 'Log out',
    },
    agreeToTerms: {
      title: 'Agree to the Terms and Conditions',
      text: 'To use Baseboard, you need to agree to the <a href="https://baseboard.pro/terms-and-conditions" target="_blank">Terms and Conditions</a>.',
      labelReadCheck: "I've read and understood the Terms and Conditions",
      buttonAgree: 'I agree to the Terms and Conditions',
    },
    apiErrorSnackbar: {
      error: {
        unknown: {
          title: 'Something went wrong',
          message:
            'An unknown error has occurred. You can retry if you want. These error are reported to us so we are aware and we will take action if needed. If this keeps happening or it is urgent feel free to reach out to our support desk.',
        },
        apiUnreachable: {
          title: 'API unreachable',
          message:
            "We're having trouble reaching our API. Sit tight, this probably won't take long.",
        },
        privileges: {
          title: 'Missing privileges',
          message:
            "You don't have the right privileges to perform this action. That's on us, we shouldn't show you things that you do not have access to. If you do need access, ask you organization admin to give them to you.",
        },
        validation: {
          title: 'Action not allowed',
          message: 'This action is not allowed in the current state.',
        },
      },
      actionMoreDetails: 'Details',
      actionDismiss: 'Dismiss',
      detailsTitle: 'Error details',
      detailsClose: 'Close',
      detailsCopy: 'Copy',
    },
    bopModulesTimeChart: {
      headerTotalTime: 'Total time',
    },
    bopModelViewer: {
      noModel: 'No 3D model associated with this BOP',
      converting: 'Converting 3D model...',
      convertingHint: 'This may take a few minutes. Refresh the page to check progress.',
      conversionFailed: '3D model conversion failed. Try re-uploading the STEP file.',
      loadingModel: 'Loading 3D model...',
      downloadFailed: 'Could not download the 3D model. Check your connection and try again.',
      modelFailed: 'The 3D model could not be opened. Try re-uploading the STEP file.',
      zoomToSelection: 'Zoom to selection',
      hidePreviousSteps: 'Hide previous',
      showSubmodule: 'Show section',
      showEverything: 'Show all',
      fullscreen: 'Fullscreen',
      exitFullscreen: 'Exit fullscreen',
      resetCamera: 'Reset camera',
      saveViewport: 'Save view',
      selectPrompt: 'Select a part',
      move: 'Move',
      rotate: 'Rotate',
      addToStep: 'Add to step',
      removeFromStep: 'Remove from step',
      allocateAllInstances: 'Allocate all instances',
      instanceOf: 'Instance {index} of {total}',
      selectInstances: 'Select instances',
      instanceSelectionPrompt:
        'Select which {required} of {total} instances of {article} to highlight',
      pickInstances: 'Select {required} instances of {article}',
      closeSelection: 'Clear selection',
      explode: 'Explode',
      autoExplode: 'Drag to spread the parts apart',
      explodeOverrideTitle: 'Override custom positions?',
      explodeOverrideMessage:
        'Using the exploded view will replace the custom article positions currently applied.',
      resetTransforms: 'Reset article positions',
      highlightMismatch: 'Highlighted parts ({highlighted}) does not match allocated ({allocated})',
      allocated: 'Article allocated to step',
      alreadyAllocated: 'This part is already allocated to this step',
      cannotAllocate: 'This part cannot be allocated (no linked article)',
      showingEbom: 'Showing: {ebomName}',
      ebomTree: 'EBOM',
      showEbomTree: 'Show EBOM tree',
      hideEbomTree: 'Hide EBOM tree',
      resizeEbomTree: 'Drag to resize, double-click to reset',
      ebomTreeNotSelectable: 'This part cannot be selected (no linked article)',
      hideArticle: 'Hide in 3D',
      showArticle: 'Show in 3D',
      hiddenWithAssembly: 'Hidden with its assembly',
      showAllArticles: 'Show all',
      controlsHelpTitle: 'Viewer controls',
      controlsHelpShow: 'Show viewer controls',
      controlsHelpHide: 'Hide viewer controls',
      controlRotate: 'Rotate',
      controlRotateGesture: 'Left-drag',
      controlPan: 'Pan',
      controlPanGesture: 'Right-drag',
      controlPanAlternative: 'or {key} + drag',
      controlZoom: 'Zoom',
      controlZoomGesture: 'Scroll',
      controlRotateTouch: 'Drag with one finger',
      controlPanTouch: 'Drag with two fingers',
      controlZoomTouch: 'Pinch',
      controlZoomToPart: 'Zoom to part',
      controlZoomToPartGesture: 'Double-click or select and press {key}',
    },
    viewerModelPicker: {
      title: 'Choose the 3D model for this step',
      menuTitle: '3D model shown in this step',
      automatic: 'automatic',
      none: 'No 3D model',
      useAutomatic: 'Use automatic selection',
      useAutomaticHint: 'Follows the articles and inputs of this step',
    },
    bopNavigationDrawer: {
      headerTimePerModule: 'Time spent per module',
      headerAssociatedEboms: 'Associated EBOMs',
      headerAssociatedIssues: 'Associated unresolved issues',
      headerAssociatedUploads: 'Associated uploads',
    },
    bopUploads: {
      addButton: 'Add upload (.STEP)',
      active: 'Active',
      queued: 'Queued',
      converting: 'Converting',
      conversionFailed: 'Conversion failed',
    },
    bopSelector: {
      buttonModules: 'Go to BOP',
      buttonBOPs: 'List BOPs',
    },
    bopReleaseConfirmation: {
      title: 'Release BOP',
      message:
        "You're about to release the BOP version {bopVersion} for product {productName} with product configuration {productConfigurationName} at site {siteName}. This is an irreversible process that makes this release read-only and will have downstream effects like the creation of article numbers for modules. Are you sure?",
    },
    bulkAllocationSuggestionActions: {
      createTitle: 'Create allocation suggestions',
      createButton: 'Create',
      createConfirmation: 'Allocation suggestions created',
      acceptTitle: 'Accept {count} matching allocation suggestion(s)',
      acceptButton: 'Accept',
      acceptConfirmation: 'Matching allocation suggestions accepted',
      rejectTitle: 'Reject {count} undecided allocations suggestion(s)',
      rejectButton: 'Reject',
      rejectConfirmation: 'Undecided suggestions rejected',
    },
    archive: {
      tabActive: 'Active',
      tabArchived: 'Archived',
      noArchivedItems: 'Nothing has been archived.',
      archiveProductMessage:
        'Archiving "{name}" moves it and all of its configurations to the archive. Archived items are read-only until they are unarchived.',
      unarchiveProductMessage:
        'Unarchiving "{name}" moves it back to the active overview so it can be edited again. Configurations that were archived individually stay archived.',
      archiveConfigurationMessage:
        'Archiving "{name}" moves it to the archive without affecting the product or its other configurations. Archived configurations are read-only until they are unarchived.',
      unarchiveConfigurationMessage:
        'Unarchiving "{name}" moves it back to the active overview so it can be edited again.',
      unarchiveConfigurationWithProductMessage:
        'The product "{product}" is archived and will be unarchived as well. Its other configurations stay archived.',
      productArchived:
        'This product is archived. The product and its configurations are read-only until it is unarchived.',
      unarchiveProduct: 'Unarchive product',
      configurationArchived:
        'This product configuration is archived, so everything in it is read-only. Unarchive it on the product configurations page to make changes.',
      openConfigurations: 'Go to configurations',
    },
    releaseConfirmationDialog: {
      confirmation: 'I understand this is an irreversible process and understand the consequences',
    },
    confirmDialog: {
      title: 'Are you sure?',
      ok: 'OK',
      cancel: 'Cancel',
    },
    completeProductionBatch: {
      title: 'Complete production batch',
      text: 'Are you sure you want to complete the production batch for production order number {poNumber} with serial number(s) {serialNumbers}?',
      incompleteChecks: 'There are incomplete checks in the following steps:',
      buttonActivator: 'Complete',
      buttonCancel: 'Cancel',
      buttonSave: 'Complete',
    },
    stepChecks: {
      noProductionBatchHint: 'Create a production batch to track progress',
    },
    createIssueDialog: {
      title: 'Create an issue',
      linkToSerialNumbers: 'Link this issue to these serial numbers',
      labels: 'Labels',
    },
    createStep: {
      title: 'Create step',
    },
    createStepUploads: {
      title: 'Add attachments to step',
    },
    csvEbomLoader: {
      headerEbom: 'EBOM',
      headerColumns: 'Columns',
      instructionColumns: 'Please provide the header names for the following columns',
      ebomName: "What's the name of this EBOM?",
      encoding: 'How is the file encoded? (UTF-8 is a safe default)',
      rootArticleNumber: 'What is the root article number',
      hierarchyType: 'How should the hierarchy be determined?',
      hierarchyTypePositionBased: 'Position-based hierarchy (preferred)',
      hierarchyTypeParentBased: 'Parent-based hierarchy',
      warningParentBased:
        'Warning: Parent-based hierarchy can cause issues if multiple rows with the same article name are present in the file. Position-based hierarchy is preferred.',
      columnArticleNumber: 'Article number',
      columnArticleName: 'Article name',
      columnQuantity: 'Quantity',
      columnArticleRevision: 'Revision',
      columnUnit: 'Unit',
      columnPosition: 'Position',
      columnParentArticleNumber: 'Parent article number',
      buttonProcess: 'Process CSV',
    },
    dataTable: {
      loading: 'Loading...',
      noData: 'No data',
      page: `{'{'}0{'}'}-{'{'}1{'}'} of {'{'}2{'}'}`,
      itemsPerPage: 'Items per page',
      moreData:
        "Showing the first {count} results. Adjust the filters if you can't find what you're looking for.",
    },
    deleteDialog: {
      title: 'Delete {name}?',
      areYouSure: 'Are you sure you want to delete {name}?',
      delete: 'Delete',
      cancel: 'Cancel',
      warningTitle: 'Be careful',
      hindranceText: 'I understand that deleting this item is dangerous and irreversible',
      deleteText: 'Are you sure you want to delete {name}?',
    },
    downloadMbomDialog: {
      title: 'Download MBOM',
      closeButton: 'Close',
      downloadBopMbom: 'Download BOP MBOM',
    },
    downloadModuleMbomButton: {
      labelShort: 'Download MBOM',
      labelLong: 'Download MBOM for {name}',
    },
    downloadWorkInstructionsDialog: {
      title: 'Export Work Instructions',
      closeButton: 'Close',
    },
    downloadModuleWorkInstructionsButton: {
      labelShort: 'Export Work Instructions',
      labelLong: 'Export Work Instructions for {name}',
    },
    duplicateModule: {
      title: 'Duplicate module',
    },
    duplicateStep: {
      title: 'Duplicate step',
    },
    ebomBulkAllocateDialog: {
      title: 'Bulk allocate EBOM parts',
      text: 'Select parts and allocate them to a step by dragging them there. Click to select or deselect one part; Click one part and Shift+Click another part to select all intermediate parts (within one assembly); Ctrl+Click / Cmd+Click an assembly to select all underlying parts; Right-Click to deselect all selected parts.',
    },
    cross3dModelWarning: {
      title: 'Different 3D model',
      body: 'This step already has parts allocated from {currentEbomName}, which uses a different 3D model than {newEbomName}. Only one 3D model can be shown per step. Which one do you want to show?',
      useCurrent: 'Use {currentEbomName}',
      useNew: 'Use {newEbomName}',
    },
    ebomDiffDialog: {
      title: 'Differences between {oldEbomName} and {newEbomName}',
      selectLabel: 'Select EBOM to compare',
      diffButton: 'Show differences',
      closeButton: 'Close',
    },
    ebomDiffSameParts: {
      title: '{count} allocated part(s) overlapping',
      swapExplanation:
        'These EBOM parts are exactly the same in EBOM {oldEbomName} and {newEbomName}. You can now easily swap the allocations from the EBOM Parts in {oldEbomName} to EBOM Parts in {newEbomName}.',
      swapButton: 'Swap',
      notificationSwapped:
        'The overlapping EBOM part allocations have been swapped to the new EBOM parts',
    },
    ebomDiffTree: {
      noDifferences: 'No differences found',
    },
    ebomLoader: {
      titlePickEbom: 'Pick EBOM',
      titleLoadEbom: 'Load EBOM',
      titlePromoteEbomParts: 'Promote parts',
      titleEbomPartAllocationSuggestions: 'Part allocation suggestions',
      titleDone: 'Done',
      loadEbomFromCsv: 'From CSV',
      loadEbomFromStepFile: 'From STEP file',
      doneHeader: 'Done!',
      doneMessage: 'The EBOM is processed.',
      doneButton: 'Close',
    },
    stepEbomLoader: {
      ebomName: 'EBOM name',
      buttonProcess: 'Generate EBOM',
    },
    ebomPartAllocationSuggestionsLoader: {
      header: 'Suggest part allocations',
      message:
        'If you previously allocated EBOM parts, we can make suggestions on where to put the current EBOM Parts. You can always trigger these suggestions later in the EBOM view. ',
      skipButton: 'Skip',
      createSuggestionsButton: 'Suggest allocations',
      successMessage: 'Created {count} suggestions',
    },
    ebomPartDetails: {
      buttonPromoteEbomPart: 'Promote',
      buttonSnoozeEbomPart: 'Snooze',
      buttonUnSnoozeEbomPart: 'Unsnooze',
      quantityAllocatedToSteps: 'Quantity allocated to steps',
      headerStepAllocations: 'Step Allocations',
      tableHeaderModule: 'Module',
      tableHeaderStep: 'Step',
      tableHeaderQuantity: 'Quantity',
      promoteDialogTitle: 'Promote to article?',
      promoteDialogContent: 'Are you sure you want to promote this to an article?',
      labelSuggestion: 'Suggestion',
    },
    ebomPartSnoozeFilters: {
      explanation: 'Only show EBOM parts where snooze status is...',
    },
    ebomPartTree: {
      title: 'Parts',
      filterOpenSuggestionsHint: 'Only show parts with open suggestions',
      filterAllocationDisparityHint: 'Only show parts with an allocation disparity',
      filterSnoozedHint: 'Only show parts with a specific snooze status',
    },
    editDialog: {
      save: 'Save',
      cancel: 'Cancel',
      title: {
        create: 'Create',
        edit: 'Edit',
        createWithEntity: 'Create {entity}',
        editWithEntity: 'Edit {entity}',
      },
    },
    helpDialog: {
      title: 'Help is near',
      text: 'We have a few channels for you to get help: <ul><li>You can report a bug or request a feature <a href="https://lindie.app/share/f1981f18ef85e8cc559868c99519f17567c82647" target="_blank">here</a>.</li><li>You can talk to us directly through <a href="https://baseboard-workspace.slack.com" target="_blank">our Slack</a> (you should have a private channel there)</li><li>And of course there\'s always good old email: support&#64;baseboard.pro</li></ul>',
    },
    issueCommentList: {
      buttonResolve: 'Resolve',
      buttonUnresolve: 'Unresolve',
    },
    iconArchived: {
      archived: 'Archived',
      notArchived: 'Not archived',
    },
    iconConsumable: {
      title: 'Consumable',
    },
    iconEbom: {
      title: 'EBOM',
    },
    iconEbomPartSnoozeStatus: {
      snoozed: 'Snoozed',
      notSnoozed: 'Not snoozed',
      matchesArticleNumberAndPath:
        'Snooze matches article number and path in EBOM of this EBOM part',
      matchesRevision: 'Snooze matches revision of this EBOM part',
      notMatchesRevision: 'Snooze does not match revision of this EBOM part',
      matchesQuantity: 'Snooze matches quantity of this EBOM part',
      notMatchesQuantity: 'Snooze does not match quantity of this EBOM part',
    },
    iconIssue: {
      title: 'Issue',
    },
    iconManual: {
      title: 'Manual',
    },
    iconPart: {
      title: 'Part',
    },
    iconProductionBatch: {
      title: 'Production batch',
    },
    mainAppBar: {
      buttonOrgHome: 'Organization home',
      buttonOperator: 'Operator home',
    },
    operatorCurrentProductionBatch: {
      title: 'Currently working on production order number {poNumber}',
      deselectButton: 'Deselect',
    },
    operatorModuleStepper: {
      finalizeTitle: 'Finalize',
      doneTitle: 'Done',
      previousButton: 'Previous',
      nextButton: 'Next',
      issueButton: 'Report an issue',
      viewChanges: 'View changes',
      diffDialogDismiss: 'Okay, Got it!',
    },
    stepChangeBanner: {
      title: 'Changes since your last visit',
      previousVersion: 'Previous version',
      currentVersion: 'Current version',
      noPreviousVersion: 'No previous version available',
      newStep: 'This step is new',
      removedStep: 'This step was removed',
    },
    operatorFontSizeSelector: {
      label: 'Font size',
      options: {
        small: 'Small',
        medium: 'Medium',
        large: 'Large',
        extraLarge: 'Extra Large',
      },
    },
    operatorProductionBatchDrawer: {
      titleBatchesInProgress: 'Your work in progress',
    },
    operatorStepTree: {
      title: 'Steps',
      changedDescription: 'This step was changed since your last visit',
      addedDescription: 'This step is new since your last visit',
      removedDescription: 'This step was removed since your last visit',
      movedDescription: 'This step was moved since your last visit',
      movedFromDescription: 'This step was moved from {position}',
    },
    organizationPermissionsPicker: {
      inheritanceExplanation: 'Inherited from',
    },
    pageHeader: {
      createButton: 'Create',
    },
    articleAllocationList: {
      source: 'Source',
      sourceEbom: 'EBOM',
      sourceManual: 'Manual',
      inEbom: 'In EBOM',
      allocatedToSteps: 'Allocated to steps',
    },
    promoteEbomParts: {
      title: 'EBOM loaded',
      instructions:
        "Below are the parts in the EBOM that are unknown Articles. You can now choose to promote them to an Article so you can later use them. A good guideline whether to promote an EBOM part is whether you intend it be part of the MBOM. Once you're finished, click DONE",
      buttonSelectNonAssemblies: 'Select non-assemblies',
      buttonPromote: 'Promote to article',
      buttonDone: 'Done',
      successMessage: 'Parts have been promoted to articles',
    },
    siteButtonPicker: {
      noData: 'First create a site for your organization',
    },
    stepPickerDialog: {
      title: 'Choose a step',
      select: 'Select this step',
    },
    stepProposalsConverter: {
      titleNoSteps: 'No instructions found',
      textNoSteps:
        "We couldn't find any instructions in the file you provided. You can try another file or use different settings for this one and try again.",
      titleSteps: 'Create steps',
      textSteps:
        'Below you see the steps that we could find in the the file you provided. Click on the Create button to add the step to your module along with the selected uploads. You can edit the steps and uploads later.',
    },
    stepProposalsFromFileDialog: {
      titlePickFile: 'Select file',
      titleLoad: 'Extract instructions',
      titleConvertToSteps: 'Create steps',
      explanationTitle: 'Create steps from file',
      explanationText:
        'You can select a file below and we will try to extract information so you can create steps with the least amount of effort. We support the following file types:',
      explanationMedia: 'Audio and video files with spoken instructions',
      explanationPdf: 'PDF files with text and images',
      explanationDocx: 'Word documents (.doc, .docx) with text and images',
      disclaimerTitle: 'Your data might leave the EU',
      disclaimerText:
        'The selected file might leave the EU region for analysis. Other than the file you provide, data processors will not receive contextual information like your organization name. For video files you provide, data processors will only receive the audio. Whichever data processors we choose, we guarantee our Terms of Service and other agreements apply, especially when it comes to intellectual property rights and confidentiality.',
      inputUpload: 'Pick a media file',
      buttonCreateProposals: 'Extract steps',
      errorTitle: 'Something went wrong',
      errorText:
        "It's a complicated process, sometimes things go wrong. Below you can find some details about the error. If that doesn't help, please reach out to support so we can help you.",
    },
    stepProposalsProgress: {
      optimizing: {
        title: 'Preparing',
        message: 'Optimizing media format for fast processing',
      },
      extracting: {
        title: 'Analyzing',
        message: 'Extracting the instructions. This might take several minutes, hang in there!',
      },
      clustering: {
        title: 'Processing',
        message: 'Bundling instructions into steps and removing redundant data',
      },
      done: {
        title: 'Done!',
        message: 'Step proposals are ready for review.',
      },
    },
    stepProposalsFromPdfLoader: {
      inputFirstPage: 'First page',
      inputLastPage: 'Last page',
      inputLanguage: 'Output instructions in',
      additionalPrompt: 'Additional instructions (optional)',
      additionalPromptHint:
        'Give the AI extra context or guidance for example: terminology to use, parts of the document to focus on, or style preferences.',
    },
    stepProposalsFromDocxLoader: {
      inputLanguage: 'Output instructions in',
      additionalPrompt: 'Additional instructions (optional)',
      additionalPromptHint:
        'Give the AI extra context or guidance for example: terminology to use, parts of the document to focus on, or style preferences.',
    },
    stepProposalsFromMediaLoader: {
      inputLanguage: 'In what language are the spoken instructions?',
      outputLanguage: 'Preferred output language',
      outputLanguageSameAsInput: 'Same as input',
      averageStepDuration: 'Average step duration',
      averageStepDurationHint:
        'Helps the AI estimate how long steps take on average. The AI may adjust this value for individual steps.',
      additionalPrompt: 'Additional instructions (optional)',
      additionalPromptHint:
        'Give the AI extra context or guidance for example: terminology to use, parts of the recording to focus on, or style preferences.',
    },
    stepTree: {
      title: 'Steps in module',
      buttonProposeFromFile: 'Create steps from file',
      buttonPreview: 'Preview steps as an operator',
      buttonAdd: 'Add step',
      buttonAddEmpty: 'Add empty step',
      buttonCreate: 'Create step',
      buttonCopy: 'Copy step',
      buttonAddFromLibrary: 'Add from library',
      buttonAddInSection: "Add step in '{section}'",
      buttonAddSection: 'Add section',
      buttonGroupIntoSection: 'Group into section ({count})',
      libraryStep: 'Library step',
      emptySectionHint: 'Empty section: drag steps here',
    },
    createSectionDialog: {
      hint: 'A section only names and orders the steps inside it. Drag steps into the section after creating it.',
    },
    updateStep: {
      sectionHint:
        'This step is a section. It only names and orders the steps inside it, so only name and type apply.',
    },
    stepPreviewDialog: {
      button: 'Preview',
      title: 'Operator preview',
      hint: 'How this step appears to an operator. Checks are shown disabled and production batch actions are left out.',
    },
    stepOutputsSection: {
      buttonAssign: 'Assign as input to a step',
      buttonSaveAndAssign: 'Save & assign',
      assignHint: 'Assign this output as input to a step in this BOP.',
      assignSameStep: 'Choose a different step than the one producing the output.',
    },
    stepDropDecisionDialog: {
      title: 'Step contains data',
      message:
        "Dropping here turns '{target}' into a section, and a section's own data is no longer used.",
      optionBundle: 'Create a new section with both steps inside',
      optionMove: "Move the step's data to the dropped step",
      optionMoveUnavailable: 'Moving data is not available for library steps.',
      optionMoveHint:
        'Part allocations, inputs, tools, instructions and duration move to the dropped step.',
      ioWarning:
        'Outputs (and inputs coming from the dropped step itself) cannot be moved and will stay on the section step.',
      summaryAllocations: 'Part allocations: {count}',
      summaryInputs: 'Inputs: {count}',
      summaryOutputs: 'Outputs: {count}',
      summaryTools: 'Tools: {count}',
      summaryInstructions: 'Instructions',
      summaryWarning: 'Warning',
      summaryIcons: 'Icons: {count}',
      summaryDuration: 'Duration',
    },
    addFromLibrary: {
      title: 'Add step from library',
      preview: 'Preview',
      empty: 'No released library steps yet. Release one in the library first.',
    },
    acceptLibraryStepUpdate: {
      buttonActivator: 'Update',
      title: 'Update library step?',
      updateInDraft: 'This BOP will use version {latest} of this step.',
      updateInNewDraft:
        'A draft of this BOP will be created with version {latest}. The released BOP stays unchanged.',
      updateAffectsAllUses:
        'This changes every use of this step in this BOP. Review them first, or unlink a use from the library if it should keep its current version.',
      usedIn: 'Used in: {modules}',
      buttonAccept: 'Accept Update',
      updateApplied: 'Update applied successfully',
      draftCreated: 'Draft BOP created with updated step. Review and release when ready.',
    },
    libraryStepStatus: {
      updateAvailable: 'Update available',
      updateAvailableMessage: 'A newer version of this library step is available',
      versions: 'Current version: v{current} · Latest version: v{latest}',
      detached: 'Detached from Library',
      detachedMessage: 'This step is a local copy and no longer receives library updates.',
    },
    libraryStepVersionChange: {
      currentVersion: 'Current version',
      newVersion: 'New version',
    },
    libraryStepReleaseConfirmation: {
      title: 'Release New Version',
      message:
        "BOPs that use an older version of this step show 'Update available'. Please review which version each BOP uses and update the ones that need it.",
      affectedBops:
        '{count} BOP uses an older version of this step. | {count} BOPs use an older version of this step.',
      bopStatus: 'BOP status',
      usedVersion: 'Uses version',
      searchPlaceholder: 'Search product, configuration or site',
      released:
        'Version released. | Version released. 1 BOP can now update to this version. | Version released. {count} BOPs can now update to this version.',
    },
    libraryStepLock: {
      title: 'Library-controlled step',
      message: 'This is a library step. Changes must be made in the Library.',
      editInLibrary: 'Edit in Library',
      unlink: 'Unlink from library',
      unlinkTitle: 'Unlink from library?',
      unlinkConfirm:
        'This creates a local, editable copy of the step in this BOP. It will no longer update when the library step changes.',
    },
    stepToolbar: {
      buttonDelete: 'Delete step',
    },
    promoteStepToModule: {
      button: 'Promote to module',
      title: 'Promote step to Module',
      hintStep: 'This step becomes the only step of the new module and leaves this module.',
      hintSubsteps:
        'This step and its {count} substeps become the steps of the new module and leave this module.',
    },
    textAreaField: {
      markdownDetails:
        'You can use <a href="https://github.github.com/gfm/" target="_blank">Markdown</a>, for instance to make your text <b>**bold**</b> <i>_italic_</i> or <s>~strikethrough~</s>',
    },
    richTextField: {
      linkUrl: 'Link URL',
      apply: 'Apply',
      placeholder: 'Pasting markdown is supported here',
    },
    uploadButton: {
      instructionsEnabled: 'Select files to upload, drag & drop them, or paste them',
      instructionsDisabled: 'You do not have permissions to upload new files',
    },
    uploadCard: {
      disableDeleteBecauseUsed:
        'Cannot delete this upload because it is used in {count} location(s). Remove it from those locations first.',
    },
    uploadPicker: {
      title: 'Pick a file',
      search: 'Search',
      maybeMoreItems: 'Only showing a limited number of items, refine your search to discover more',
      selectButton: 'Select',
      closeButton: 'Close',
      newUploadButton: 'Upload new file',
      tableItemsPerPage: 'Items per page',
      tableNoData: 'No uploads found',
    },
    upsertDialog: {
      title: {
        create: 'Create',
        update: 'Edit',
        createWithEntity: 'Create {entity}',
        updateWithEntity: 'Edit {entity}',
      },
    },
    userProductionBatches: {
      buttonOtherUsers: 'Find batches assigned to other users',
      buttonTransferBatch: 'Transfer batch to you',
      confirmTransferText: 'Transfer batch with production order number {poNumber} to you?',
    },
    userPermissions: {
      title: 'Permissions',
      organizationPermissions: 'Organization permissions',
      addOrganization: 'Add organization',
      field: {
        userPermissions: 'User permissions',
      },
    },
    versionStatus: {
      status: {
        RELEASED: 'Released',
        DRAFT: 'Draft',
      },
    },
  },
  validation: {
    abbreviation: {
      regex: 'An abbreviation should be between 1 and 12 characters',
      unique: 'This abbreviation is already taken',
    },
    filename: {
      regex:
        'A filename can only contain letters, numbers, and these characters: -, _, +, .; it also needs to end in an extension of letters, numbers, and `_`',
    },
    isChecked: {
      notChecked: 'This should be checked',
    },
    maxDifference: {
      message: 'You can process a maximum of {difference} pages at a time.',
    },
    milliQuantity: {
      min: 'Value should be at least {min}',
      max: 'Value should be at most {max}',
    },
    normalName: {
      regex: 'A name should be between 1 and 150 characters',
    },
    normalDescription: {
      regex: 'A description should be between 0 and 255 characters',
    },
    orgSlug: {
      pattern:
        'The slug must be between 3 and 50 characters and can only contain: lowercase letters, numbers, -',
      taken: 'This slug is already taken',
    },
    unique: {
      unique: 'Should be unique and this is already taken',
    },
    userEmail: {
      taken: 'This email is already taken by another user',
    },
  },
  permission: {
    ORG_ADMIN: {
      name: 'Administrator [DANGEROUS]',
      description:
        'Administrators have access to everything of the organization, including user and permission management.',
    },
    ORG_ARTICLE_READ: {
      name: 'View articles',
      description:
        "Can view the entire articles database. Users that only need to view products don't need this extra permission.",
    },
    ORG_ARTICLE_WRITE: {
      name: 'Write articles',
      description: 'Create and edit articles in the articles database.',
    },
    ORG_ARTICLE_DELETE: {
      name: 'Delete articles',
      description: 'Delete articles from the articles database (if not used in released BOPs)',
    },
    ORG_EBOM_READ: {
      name: 'View EBOMs',
      description:
        "View the entire EBOM. Users that only need to view products, don't need this extra permission.",
    },
    ORG_EBOM_WRITE: {
      name: 'Write EBOMs',
      description: 'Load EBOMs from external sources',
    },
    ORG_EBOM_DELETE: {
      name: 'Delete EBOMs',
      description: 'Delete EBOMs from unreleased BOPs',
    },
    ORG_ISSUE_COMMENT: {
      name: 'Comment on issues',
      description:
        'Create new comments on issues, update and delete own comments and mark issues as resolved.',
    },
    ORG_ISSUE_READ: {
      name: 'View issues',
      description: 'View issues on BOPs, steps, articles and serial numbers',
    },
    ORG_ISSUE_WRITE: {
      name: 'Write issues',
      description: 'Create new issues and update own issues.',
    },
    ORG_ISSUE_DELETE: {
      name: 'Delete issues',
      description: 'Delete own issues',
    },
    ORG_MBOM_READ: {
      name: 'View MBOMs',
      description: 'Export MBOMs of released BOPs.',
    },
    ORG_MEMBER: {
      name: 'Member',
      description: 'Is a member of the organization, needed for basic access.',
    },
    ORG_PRODUCTION_BATCH_READ: {
      name: 'View production batches',
      description: 'View all production batches',
    },
    ORG_PRODUCTION_BATCH_WRITE: {
      name: 'Write production batches',
      description: 'Create new production batches, transfer batch to themselves and update it',
    },
    ORG_PRODUCTION_BATCH_DELETE: {
      name: 'Delete production batches',
      description: 'Delete any production batch',
    },
    ORG_PRODUCT_READ: {
      name: 'View products',
      description: 'View BOPs, modules and steps.',
    },
    ORG_PRODUCT_WRITE: {
      name: 'Write products',
      description:
        'Create and update BOPs, modules, steps and tools. Associate articles, tools, etc to steps.',
    },
    ORG_PRODUCT_DELETE: {
      name: 'Delete products [DANGEROUS]',
      description: 'Delete entire BOPs, modules and steps with all underlying entities.',
    },
    ORG_UPLOAD_OWN: {
      name: 'Own uploads',
      description:
        'Create new uploads and view, rename and delete only the uploads they created themselves.',
    },
    ORG_UPLOAD_INDEX: {
      name: 'View uploads',
      description:
        'View all of the organizations uploads. Needed to associate attachments to steps and issues.',
    },
    ORG_UPLOAD_WRITE: {
      name: 'Write uploads',
      description: "Create new uploads in the organization's upload library",
    },
    ORG_UPLOAD_DELETE: {
      name: 'Delete uploads',
      description: 'Delete uploads that are not linked to released BOPs',
    },
  },
  entity: {
    issueLabel: {
      singular: 'Issue Label',
      plural: 'Issue Labels',
      deleteWarning:
        'Cannot delete a label that is assigned to issues, please remove the label from the issues first.',
      field: {
        name: 'Name',
        color: 'Color',
      },
    },
    article: {
      singular: 'Article',
      plural: 'Articles',
      field: {
        articleNumber: 'Article Number',
        revision: 'Revision',
        name: 'Name',
        unit: 'Unit',
        quantity: 'Quantity',
        type: 'Type',
        source: 'Source',
      },
      type: {
        PART: 'Part',
        CONSUMABLE: 'Consumable',
      },
      unit: {
        full: {
          GRAM: 'Gram',
          KILOGRAM: 'Kilogram',
          LITER: 'Liter',
          MILLILITER: 'Milliliter',
          METER: 'Meter',
          MILLIMETER: 'Millimeter',
          PIECES: 'Pieces',
        },
        abbreviated: {
          GRAM: 'GR',
          KILOGRAM: 'KG',
          LITER: 'L',
          MILLILITER: 'ML',
          METER: 'M',
          MILLIMETER: 'MM',
          PIECES: 'PCS',
        },
      },
      validation: {
        articleAndRevisionTaken: 'An article already exists with this article number and revision',
      },
      disableEditBecauseReleased: 'Cannot edit this article because it is used in a released BOP',
      disableDeleteBecauseReleased:
        'Cannot delete this article because it is used in a released BOP',
    },
    bop: {
      singular: 'Bill of Process',
      plural: 'Bills of Process',
      deleteWarning:
        'When you delete a Bill of Process, you will lose the process tree of this BOP, all Steps in that tree that are not part of other BOPs, reviews and their comments, etc. Make absolutely sure this is what you want to do.',
      field: {
        version: 'Version',
        versionStatus: 'Status',
      },
    },
    check: {
      singular: 'Check',
      plural: 'Checks',
      uploadButton: 'Upload file',
      uploadUsage: 'Check attachment',
      field: {
        poNumber: 'Production Order Number',
        serialNumber: 'Serial Number',
        reportingUser: 'Reporting User',
        released: 'Completed',
        value: 'Value',
        confirmed: 'Confirmed',
        milliQuantity: 'Quantity',
        text: 'Text',
        upload: 'Attachment',
        updatedAt: 'Updated',
        createdAt: 'Created',
      },
    },
    checkConfiguration: {
      singular: 'Check',
      plural: 'Checks',
      suggestions: 'Check suggestions',
      field: {
        type: 'Type',
        description: 'Description',
        lowerLimit: 'Lower limit',
        upperLimit: 'Upper limit',
        unit: 'Unit',
        isRequired: 'Required',
        allowedMimeTypes: 'Allowed file types',
      },
      type: {
        CONFIRMATION: 'Confirm',
        NUMERIC: 'Numeric value',
        STRING: 'Text value',
        FILE_UPLOAD: 'File upload',
      },
      fileTypeOption: {
        any: 'Any file type',
        imageFiles: 'Image files',
        pdf: 'PDF',
      },
    },
    ebom: {
      singular: 'EBOM',
      plural: 'EBOMs',
      deleteWarning:
        'When you delete this EBOM, all its EBOM Parts will be unallocated from the steps',
      field: {
        name: 'Name',
      },
    },
    ebomPart: {
      singular: 'EBOM Part',
      plural: 'EBOM Parts',
      isPromoted: 'Is promoted to Article',
      field: {
        articleNumber: 'Article number',
        articlePath: 'Path in EBOM',
        name: 'Name',
        quantity: 'Quantity',
        revision: 'Revision',
        position: 'Position',
        snoozeStatus: 'Is snoozed',
      },
    },
    ebomPartAllocation: {
      singular: 'EBOM Part Allocation',
      plural: 'EBOM Part Allocations',
    },
    ebomPartSnooze: {
      status: {
        NONE: 'No snooze',
        PATH_ARTICLE: 'Snooze matches article number and path in EBOM',
        PATH_ARTICLE_REVISION: 'Snooze matches article number, path in EBOM and revision',
        PATH_ARTICLE_REVISION_QUANTITY:
          'Snooze matches article number, path in EBOM, revision and quantity',
      },
    },
    issue: {
      singular: 'Issue',
      plural: 'Issues',
      field: {
        name: 'Name',
        createdAt: 'Created',
        createdByUser: 'Created by',
        updatedAt: 'Updated',
        assignedToUsers: 'Assigned to',
        description: 'Description',
        links: 'Linked to',
        resolvedAt: 'Resolved',
        resolved: 'Resolved',
        uploads: 'Attachments',
      },
      filter: {
        assignedToUser: 'Assigned to user',
        isResolved: 'Is resolved',
        poNumbers: 'Involves production order numbers (separate with `,`)',
        serialNumbers: 'Involves serial numbers (separate with `,`)',
        bopId: 'BOP',
      },
    },
    issueComment: {
      singular: 'Comment',
      plural: 'Comments',
      field: {
        createdAt: 'Created',
        createdByUser: 'Created by',
        description: 'Description',
        resolvedAt: 'Resolved',
        resolved: 'Resolved',
        uploads: 'Attachments',
        uploadIds: 'Uploads',
        updatedAt: 'Updated',
      },
      uploadUsage: 'Issue comment',
    },
    graphNode: {
      singular: 'Node',
      plural: 'Nodes',
      field: {
        milliQuantity: 'Quantity',
      },
    },
    issueLink: {
      singular: 'Link',
      plural: 'Links',
      field: {
        bopId: 'BOP',
        moduleId: 'Module',
        poNumber: 'Production Order Number',
        serialNumber: 'Serial Number',
        serialNumbers: 'Serial Numbers',
        articleId: 'Article',
      },
      type: {
        article: 'To article',
        bop: 'To BOP',
        module: 'To module',
        step: 'To step',
        serialNumber: 'To production order & serial number',
      },
    },
    manualPartAllocation: {
      singular: 'Manual Part Allocation',
      plural: 'Manual Part Allocations',
    },
    module: {
      singular: 'Module',
      plural: 'Modules',
      deleteWarning:
        'When you delete a Module, you delete all underlying entities like Steps, their relations to Articles, etc. Make absolutely sure this is what you want to do.',
      field: {
        name: 'Name',
        abbreviation: 'Abbreviation',
        description: 'Description',
      },
    },
    organization: {
      singular: 'Organization',
      plural: 'Organizations',
      deleteWarning:
        'When you delete an organization, you delete ABSOLUTELY EVERYTHING belonging to it, like its Products, Product Configurations, Sites, BOPs, Checks, etc. This is IRREVERSIBLE and about the MOST DANGEROUS THING you can do on this platform. Make REALLY, ABSOLUTELY, UNEQUIVOCALLY, WITHOUT A DOUBT sure this is what you want to do.',
      field: {
        name: 'Name',
        slug: 'Slug',
      },
    },
    product: {
      singular: 'Product',
      plural: 'Products',
      deleteWarning:
        'When you delete a Product, you delete all underlying entities like Product Configurations, Bills of Process, Steps, etc. Make absolutely sure this is what you want to do.',
      field: {
        name: 'Name',
        abbreviation: 'Abbreviation',
        description: 'Description',
        image: 'Cover image',
      },
    },
    productConfiguration: {
      singular: 'Product Configuration',
      plural: 'Product Configurations',
      deleteWarning:
        'When you delete a product configuration, you delete all underlying entities like the bill of process. Make absolutely sure this is what you want to do.',
      field: {
        name: 'Name',
        abbreviation: 'Abbreviation',
        description: 'Description',
        image: 'Cover image',
      },
    },
    productionBatch: {
      singular: 'Production batch',
      plural: 'Production batches',
      exportExcel: 'Export to Excel',
      exportFailed: 'Could not export production batches. Please try again.',
      field: {
        poNumber: 'Production order number',
        serialNumbers: 'Serial numbers',
        trackSerialNumbers: 'Track individual serial numbers',
        completed: 'Is completed',
        assignedUser: 'Assigned user',
        duration: 'Duration',
      },
      filter: {
        poNumber: 'Production order number contains',
        serialNumbers: 'Serial number contains',
        completed: 'Is completed',
      },
      label: {
        noSerialNumbers: 'Checks (no serial numbers)',
      },
    },
    site: {
      singular: 'Site',
      plural: 'Sites',
      conceptExplanation: 'Sites are pl',
      deleteWarning:
        'When you delete a Site, you delete all associated entities like Product Configurations, Bills of Process, Steps, Tooling, etc. Make absolutely sure this is what you want to do.',
      field: {
        name: 'Name',
        abbreviation: 'Abbreviation',
        archived: 'Archived',
      },
    },
    icon: {
      singular: 'Icon',
      plural: 'Icons',
      deleteWarning:
        'The icon is removed from the library and no longer shown in work instructions. Steps that use it will show it as missing.',
      missing: 'Removed from the icon library',
      uploadUsage: 'Company icon',
      field: {
        name: 'Name',
        image: 'Image',
      },
    },
    stepType: {
      singular: 'Step Type',
      plural: 'Step Types',
      editWarning:
        'This Step Type is already used in a released BOP. If you change it, those changes will be propagated to all those BOPs. Make sure this is what you want to do. Alternatively, you can archive this Step Type and create a new one for future use.',
      disableDeleteBecause:
        'This Step Type is used in one or more released BOPs and cannot de deleted',
      deleteWarning:
        'When you delete a Step Type, you delete all associated entities like Steps and all their associated entities like Part Allocations, Check Configurations, etc. Make absolutely sure this is what you want to do.',
      field: {
        name: 'Name',
        archived: 'Archived',
        released: 'Released',
      },
    },
    step: {
      singular: 'Step',
      plural: 'Steps',
      field: {
        name: 'Name',
        stepType: 'Type',
        durationInSeconds: 'Duration',
        instructionText: 'Instructions',
        images: 'Images',
        tools: 'Tools',
        warning: 'Warning',
        warningText: 'Warning message',
        icons: 'Icons',
      },
    },
    stepSection: {
      singular: 'Section',
      plural: 'Sections',
    },
    stepInput: {
      singular: 'Input from Step',
      plural: 'Inputs from Steps',
      field: {
        milliQuantity: 'Quantity',
      },
    },
    stepOutput: {
      singular: 'Output of Step',
      plural: 'Outputs of Step',
      field: {
        milliQuantity: 'Quantity',
      },
    },
    stepUpload: {
      singular: 'Attachment',
      plural: 'Attachments',
      field: {
        caption: 'Caption',
        fragmentStart: 'Start at',
        fragmentEnd: 'End at',
        fragmentLoop: 'Loop',
      },
      snapshot: {
        dialogTitle: 'Create Snapshot',
        caption: 'Caption',
        noPermission: 'You do not have permission to create a snapshot',
        created: 'Snapshot created',
      },
      imageEditor: {
        title: 'Edit image',
        mode: {
          annotate: 'Annotate',
          crop: 'Crop',
        },
        tool: {
          select: 'Select',
          text: 'Text',
          rectangle: 'Rectangle',
          ellipse: 'Ellipse',
          line: 'Line',
          arrow: 'Arrow',
        },
        button: {
          color: 'Color',
          thickness: 'Thickness',
          undo: 'Undo',
          redo: 'Redo',
          delete: 'Delete selected',
          resetCrop: 'Reset crop',
          removeEdits: 'Remove all edits',
          maximize: 'Maximize',
          minimize: 'Restore size',
        },
        hint: {
          annotate:
            'Pick a tool and drag on the image. Click an annotation to move, resize or restyle it. Double-click text to edit it.',
          crop: 'Drag the edges or corners of the frame to crop. The original image is always kept.',
        },
        discardConfirmText: 'Discard unsaved edits to this image?',
        imageLoadFailed: 'Could not load the image. Please try again.',
      },
      uploadUsage: 'Step attachment',
    },
    bopUpload: {
      uploadUsage: 'BOP file',
    },
    tool: {
      singular: 'Tool',
      plural: 'Tools',
      editWarning:
        'This Tool is already used in a released BOP. If you change it, those changes will be propagated to all those BOPs. Make sure this is what you want to do. Alternatively, you can archive this Tool and create a new one for future use.',
      disableDeleteBecause: 'This Tool is used in one or more released BOPs and cannot de deleted',
      deleteWarning:
        'When you delete a Tool, you delete it from all the Steps it is used in (but not the steps themselves). Make absolutely sure this is what you want to do.',
      field: {
        name: 'Name',
        description: 'Description',
        site: 'Site',
        images: 'Images',
        archived: 'Archived',
        released: 'Released',
      },
    },
    upload: {
      singular: 'Upload',
      plural: 'Uploads',
      field: {
        name: 'Name',
        contentType: 'Content type',
        createdAt: 'Created at',
        usageLocations: 'Usage locations',
      },
      usageLocations: {
        none: 'No usage',
      },
    },
    toolUpload: {
      singular: 'Tool attachment',
      plural: 'Tool attachments',
    },
    user: {
      singular: 'User',
      plural: 'Users',
      field: {
        name: 'Name',
        firstname: 'Firstname',
        lastname: 'Lastname',
        email: 'Email',
        needsPassword: 'Initialize password (SSO users will not need this)',
      },
    },
  },
}
